/* istanbul ignore file */
/**
 * Created by rathompson on 11/3/21
 * File: AddPermissionsTable
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import React, {
  Dispatch, EventHandler,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  VFC
} from 'react'
import {
  Button,
  Tab,
  TabList,
  TabPanel,
  Title,
  useModal
} from '@panwds/react-ui'
import {
  BUTTON_ADD_PERMISSIONS,
  BUTTON_CLONE_ROLE,
  BUTTON_NO_ACCESS,
  BUTTON_READ_ONLY,
  BUTTON_READ_WRITE,
  BUTTON_REMOVE,
  PLACEHOLDER_SEARCH_PERMISSION,
  TITLE_PERMISSIONS,
} from '../../../../constants/LabelConstants'
import { ARRAY, OBJECT } from '../../../../../common/utils/Utils'
import WebTable from './WebTable'
import APITable from './APITable'
import CloneRoleModal from '../../modal/CloneRoleModal'
import { ACCESS_LEVELS } from '../../../../constants/IAMConstants'
import GlobalContext from '../../../../../common/context/GlobalContext'
import AddPermissionsModal from '../../modal/AddPermissionsModal'
import { cloneDeep, get } from 'lodash'
import { InstanceTableFilter } from '../../../../../common/components/table/tableFilter/ConnectedTableFilter'
import type {
  IAM_Permission,
  IAM_PermissionSet,
  IAM_Role
} from '../../../../../common/reducers/types/IAMReducer.types'
import type { RowInstance, TableInstance, TableState } from '@panwds/react-table'
import type { Row } from 'react-table'
import { canUseIpAllow, FF_CUSTOM_ROLES_API } from '../../../../../common/utils/FeatureFlags'
import { getVarValue } from '../../../../../common/utils/Sparky'
import type { I_TabItem } from '../../../common/types'
import styled from 'styled-components'

const enableCustomAPI = getVarValue(FF_CUSTOM_ROLES_API, null)

interface I_Tabs { WEB: I_TabItem, API: I_TabItem }
const TABS: I_Tabs = OBJECT.deepFreeze({
  WEB: { id: 'web', label: 'Web UI' },
  API: { id: 'api', label: 'API' }
})

const CONTAINER = styled.div`
  div[role="tabpanel"] {
    margin: 0 !important;
  }
`

const APIControls: VFC<{
  tableRef: any,
  tableState: any,
  onAddPermissionsClick: EventHandler<any>,
  remainingPermissions: IAM_Permission[],
  onRemoveClick: Function
}> = ({ tableRef, tableState, onAddPermissionsClick, remainingPermissions, onRemoveClick }) => {
  return (
    <div className='cm-flex cm-gap-2'>
      <InstanceTableFilter
        placeholder={PLACEHOLDER_SEARCH_PERMISSION}
        instance={tableRef.current}/>
      <Button
        appearance='secondary'
        onClick={onAddPermissionsClick}
        disabled={!ARRAY.hasArray(remainingPermissions)}
      >{BUTTON_ADD_PERMISSIONS}</Button>
      <Button
        appearance='secondary-destructive'
        onClick={() => {
          const rows = Object.entries(tableState?.selectedRowIds)
            .filter(([id, selected]) => selected)
            .map(([id, selected]) => tableRef.current?.rowsById[id])
          onRemoveClick(rows)
        }}
        disabled={!ARRAY.hasArray(tableRef?.current?.selectedFlatRows)}
      >{BUTTON_REMOVE}</Button>
    </div>
  )
}

const WebControls: VFC<{
  tableRef: any,
  tableState: any,
  onCloneRoleClick: EventHandler<any>
}> = ({ tableRef, tableState, onCloneRoleClick }) => {

  const selectedRowIds = useMemo(() => {
    return ARRAY.clean(Object.keys(tableState?.selectedRowIds || {}))
  }, [tableState])

  const legalOptions = useMemo(() => {
    const selectedRows = ARRAY.clean(tableRef.current?.flatRows?.filter((row: any) => selectedRowIds?.includes(row.id) && !!row?.state?.access_level))
    let options = [ACCESS_LEVELS.WRITE, ACCESS_LEVELS.READ, ACCESS_LEVELS.NONE]
    for (const row of selectedRows) {
      if (row.original?.parent) {
        const parentRow = tableRef.current?.flatRows?.find((r: any) => r?.original?.name === row?.original?.parent)
        if (parentRow && !selectedRowIds.includes(parentRow.id)) {
          if (parentRow.state.access_level === ACCESS_LEVELS.READ || parentRow.state.access_level === ACCESS_LEVELS.WRITE) {
            options = options.filter(option => option !== ACCESS_LEVELS.NONE)
          }
          if (parentRow.state.access_level === ACCESS_LEVELS.WRITE) {
            options = options.filter(option => option !== ACCESS_LEVELS.NONE && option !== ACCESS_LEVELS.READ)
          }
        }
      }
    }
    return options
  }, [selectedRowIds, tableRef])

  const setAccessLevel = (access_level: string) => {
    for (const row_id of selectedRowIds) {
      tableRef.current?.setRowState(row_id, { access_level })
    }
  }

  if (ARRAY.hasArray(selectedRowIds)) {
    return (
      <div className='cm-flex cm-gap-2'>
        <Button
          appearance='secondary'
          onClick={() => setAccessLevel(ACCESS_LEVELS.WRITE)}
          disabled={!legalOptions.includes(ACCESS_LEVELS.WRITE)}
        >{BUTTON_READ_WRITE}</Button>
        <Button
          appearance='secondary'
          onClick={() => setAccessLevel(ACCESS_LEVELS.READ)}
          disabled={!legalOptions.includes(ACCESS_LEVELS.READ)}
        >{BUTTON_READ_ONLY}</Button>
        <Button
          appearance='secondary'
          onClick={() => setAccessLevel(ACCESS_LEVELS.NONE)}
          disabled={!legalOptions.includes(ACCESS_LEVELS.NONE)}
        >{BUTTON_NO_ACCESS}</Button>
      </div>
    )
  }

  if (enableCustomAPI) {
    return (
      <Button appearance='secondary' onClick={onCloneRoleClick}>{BUTTON_CLONE_ROLE}</Button>
    )
  }
  return <></>
}

const Header: VFC<{
  webTableRef: any,
  apiTableRef: any,
  activeTabId: string,
  webTableState: TableState<IAM_PermissionSet>,
  apiTableState: TableState<IAM_Permission>,
  onAddPermissionsClick: EventHandler<any>,
  onCloneRoleClick: EventHandler<any>,
  remainingPermissions: IAM_Permission[],
  onRemoveClick: Function,
  viewOnly?: boolean,
  title?: string,
  enableSearch?: boolean
}> = ({
  webTableRef,
  apiTableRef,
  activeTabId,
  webTableState,
  apiTableState,
  onAddPermissionsClick,
  onCloneRoleClick,
  remainingPermissions,
  onRemoveClick,
  viewOnly,
  title,
  enableSearch
}) => {

  const rightControls = useMemo(() => {
    switch (activeTabId) {
      case TABS.API.id:
        return (
          <APIControls
            tableRef={apiTableRef}
            tableState={apiTableState}
            onAddPermissionsClick={onAddPermissionsClick}
            onRemoveClick={onRemoveClick}
            remainingPermissions={remainingPermissions}
          />
        )
      case TABS.WEB.id:
      default:
        return (
          <WebControls
            tableRef={webTableRef}
            tableState={webTableState}
            onCloneRoleClick={onCloneRoleClick}
          />
        )
    }
  }, [activeTabId, apiTableRef, apiTableState, onAddPermissionsClick, onCloneRoleClick, onRemoveClick, remainingPermissions, webTableRef, webTableState])

  return (
    <div className='cm-p-3 cm-flex cm-justify-between cm-flex-row cm-border-l cm-border-r cm-border-t cm-border-b-0 cm-border-gray-300 cm-border-solid cm-rounded-t-lg'>
      <div className='cm-flex cm-gap-2 cm-items-baseline'>
        <Title level={1} size={'xs'} addClassName='cm-flex cm-truncate'>{title || TITLE_PERMISSIONS}</Title>
      </div>
      {viewOnly ? <></> : rightControls}
      {enableSearch && (
        <div>
          <InstanceTableFilter
            placeholder={PLACEHOLDER_SEARCH_PERMISSION}
            instance={activeTabId === TABS.API.id ? apiTableRef.current : webTableRef.current} />
        </div>
      )}
    </div>
  )
}

/**
 *   Selectable permissions table
 */
const AddPermissionsTable: VFC<{
  role: IAM_Role | undefined,
  setRole: Dispatch<SetStateAction<IAM_Role | undefined>>,
  setWebData: Dispatch<SetStateAction<IAM_PermissionSet[]>>,
  setAPIData: Dispatch<SetStateAction<IAM_Permission[]>>,
  viewOnly?: boolean,
  useRoleData?: boolean,
  title?: string,
  enableSearch?: boolean
}> = ({
  role,
  setRole,
  setWebData,
  setAPIData,
  viewOnly = false,
  useRoleData = false,
  title = '',
  enableSearch = false
}) => {

  const { iamReducer, treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer
  const { permissionSets, permissionsTree, permissions: _permissions, permissionsMap } = iamReducer

  const cloneRoleModal: { openModal: Function } = useModal(CloneRoleModal)
  const addPermissionsModal: { openModal: Function } = useModal(AddPermissionsModal)

  const [activeTabId, setActiveTabId] = useState(TABS.WEB.id)

  const webTableRef = useRef<TableInstance<IAM_PermissionSet>>()
  const apiTableRef = useRef<TableInstance<IAM_Permission>>()

  const [webTableState, setWebTableState] = useState<TableState<IAM_PermissionSet>>({} as TableState<IAM_PermissionSet>)
  const [apiTableState, setAPITableState] = useState<TableState<IAM_PermissionSet>>({} as TableState<IAM_PermissionSet>)

  const [explicitPermissions, setExplicitPermissions] = useState<IAM_Permission[]>([])
  const [webPermissionSets, setWebPermissionSets] = useState<IAM_PermissionSet[]>(permissionSets)

  const permissions: IAM_Permission[] = useMemo(() => {
    return _permissions.filter((permission: IAM_Permission) => !permission.name.startsWith('enterprise_browser'))
  }, [_permissions])

  useEffect(() => {
    const updateRow = (row: Row<IAM_PermissionSet>, access_level: string) => {
      const updateChildren = (subRows: Row<IAM_PermissionSet>[], access_level: string) => {
        subRows.forEach(row => {
          const set = webPermissionSets.find((set: IAM_PermissionSet) => row?.original.id === set?.id) as IAM_PermissionSet
          webTableRef.current?.setRowState([row.id], { access_level: set?.access_level || access_level })
          if (ARRAY.hasArray(row?.subRows)) {
            updateChildren(row?.subRows, access_level)
          }
        })
      }

      webTableRef.current?.setRowState([row.id], { access_level })
      if (ARRAY.hasArray(row?.subRows)) {
        updateChildren(row?.subRows, access_level)
      }
    }
    if (ARRAY.hasArray(webPermissionSets)) {
      for (const set of webPermissionSets) {
        const row = webTableRef?.current?.flatRows?.find(r => r?.original?.id === set?.id)
        if (row) {
          updateRow(row, set?.access_level || '')
        }
      }
    }
  }, [webPermissionSets, webTableRef])

  useEffect(() => {
    if (role && OBJECT.hasObject(role)) {
      const permission_sets = cloneDeep(permissionSets).filter(set => (useRoleData ? set.name.startsWith((role as any).app_id) : true))

      const updateChildren = (children: string[], access_level: string) => {
        if (ARRAY.hasArray(children)) {
          for (const child_id of children) {
            const child = permission_sets.find(({ name }) => name === child_id)
            if (child) {
              child.access_level = access_level
              updateChildren(child.children, access_level)
            }
          }
        }
      }

      const read_only_permission_sets: IAM_PermissionSet[] = ARRAY.clean(role.read_only_permission_sets)
      const read_write_permission_sets: IAM_PermissionSet[] = ARRAY.clean(role.read_write_permission_sets)
      const explicit_permissions: string[] = ARRAY.clean(role.explicit_permissions)

      for (const read_only of read_only_permission_sets) {
        const permissionSet = permission_sets.find(({ name }) => name === read_only.id)
        if (permissionSet) {
          permissionSet.access_level = ACCESS_LEVELS.READ
          updateChildren(permissionSet.children, ACCESS_LEVELS.READ)
        }
      }

      for (const read_write of read_write_permission_sets) {
        const permissionSet = permission_sets.find(({ name }) => name === read_write.id)
        if (permissionSet) {
          permissionSet.access_level = ACCESS_LEVELS.WRITE
          updateChildren(permissionSet.children, ACCESS_LEVELS.WRITE)
        }
      }

      setWebPermissionSets(permission_sets)
      setExplicitPermissions(explicit_permissions.map(permission => ({ ...permissionsMap[permission], is_leaf: true })))
    }
  }, [permissionSets, permissionsMap, role, useRoleData])

  const webTableInstanceRef = useCallback((node) => {
    if (node) {
      webTableRef.current = node
      if (webTableRef.current?.state) {
        setWebTableState(webTableRef.current?.state)
      }
    }
  }, [])

  const apiTableInstanceRef = useCallback((node) => {
    if (node) {
      apiTableRef.current = node
      if (apiTableRef.current?.state) {
        setAPITableState(apiTableRef.current.state)
      }
    }
  }, [])

  const webData = useMemo(() => {
    const seen: { [name: string]: boolean } = {}
    const showIpAllow = canUseIpAllow(selectedTreeNode)

    const getChildren: (children: string[], array: IAM_PermissionSet[]) => IAM_PermissionSet[] = (children, array) => {
      return children.filter(childId => (showIpAllow ? true : childId !== 'common_services.ip_restriction_policy')).map(childId => {
        const child = cloneDeep(webPermissionSets.find((set: IAM_PermissionSet) => set.name === childId)) as IAM_PermissionSet
        if (ARRAY.hasArray(child?.children)) {
          child.child_instances = getChildren(child?.children, array)
          child.child_instances.forEach((child: IAM_PermissionSet) => {
            seen[child.name] = true
            const index = array.findIndex(item => item.name === child.name)
            if (index >= 0) {
              array.splice(index, 1)
            }
          })
        }
        return child
      })
    }

    return [...webPermissionSets].filter(permSet => (showIpAllow ? true : permSet.name !== 'common_services.ip_restriction_policy')).reduce((array: IAM_PermissionSet[], value) => {
      if (seen[value.name]) {
        return array
      }
      seen[value.name] = true
      const object = cloneDeep(value) as IAM_PermissionSet
      object.access_level = value.access_level || ACCESS_LEVELS.NONE
      object.selected = value.selected || false
      if (Array.isArray(object?.children)) {
        object.child_instances = ARRAY.clean(getChildren(object?.children, array))
        object.child_instances?.forEach((child: IAM_PermissionSet) => {
          seen[child.name] = true
          const index = array.findIndex((item: IAM_PermissionSet) => item.name === child.name)
          if (index >= 0) {
            array.splice(index, 1)
          }
        })
        array.push(object)
      }
      return array
    }, [])
  }, [selectedTreeNode, webPermissionSets])

  const webUIPermissions: string[] = useMemo(() => {
    const webUIPermissions = []
    if (OBJECT.hasObject(webTableState?.rowState)) {
      for (const [rowId, rowState] of Object.entries(webTableState.rowState || {})) {
        const access_level = (rowState as any).access_level
        const row = webTableRef.current?.flatRows.find(r => r.id === rowId) as RowInstance<IAM_PermissionSet>
        if (access_level === ACCESS_LEVELS.READ) {
          const readPermissions = (row.original.permissions?.filter(permission => permissionsMap[permission].access_types?.includes(ACCESS_LEVELS.READ)) || [])
          webUIPermissions.push(...readPermissions)
        }
        if (access_level === ACCESS_LEVELS.WRITE) {
          const writePermissions = (row.original.permissions?.filter(permission => {
            return permissionsMap[permission].access_types?.includes(ACCESS_LEVELS.WRITE) ||
              permissionsMap[permission].access_types?.includes(ACCESS_LEVELS.READ)
          }) || [])
          webUIPermissions.push(...writePermissions)
        }
      }
    }
    return Array.from(new Set(webUIPermissions))
  }, [permissionsMap, webTableState.rowState])

  const apiData: IAM_Permission[] = useMemo(() => {
    return [...explicitPermissions, ...webUIPermissions.map(permission => ({ ...permissionsMap[permission], locked: true } as IAM_Permission))]
  }, [explicitPermissions, webUIPermissions, permissionsMap])

  useEffect(() => {
    if (setWebData && typeof setWebData === 'function') {
      const permission_sets: IAM_PermissionSet[] = ARRAY.clean(webTableRef?.current?.flatRows)
        .filter((row: RowInstance<IAM_PermissionSet>) => get(row, 'state.access_level', 'none') !== 'none')
        .map((row: RowInstance<IAM_PermissionSet>) => Object.assign({}, { name: row.original.name, access_level: row.state?.access_level }))
      setWebData(permission_sets)
    }
    if (setAPIData && typeof setAPIData === 'function') {
      setAPIData(apiData)
    }
  }, [apiData, setAPIData, setWebData, webData, webPermissionSets, webUIPermissions])

  const onActiveChange = useCallback(id => {
    setActiveTabId(id)
  }, [])

  const onAddPermissionSave = useCallback((permissions: IAM_Permission[]) => {
    setExplicitPermissions([...explicitPermissions, ...permissions])
  }, [explicitPermissions])

  const remainingPermissions = useMemo(() => {
    return permissions.filter(({ name }) => !apiData.find(item => item?.name === name))
  }, [apiData, permissions])

  const onAddPermissionsClick = useCallback(() => {
    addPermissionsModal.openModal({ data: remainingPermissions, onAddPermissionSave })
  }, [addPermissionsModal, onAddPermissionSave, remainingPermissions])

  const onCloneRoleSubmit = useCallback((role) => {
    setRole(role)
  }, [setRole])

  const onCloneRoleClick = useCallback(() => {
    cloneRoleModal.openModal({ onSubmit: onCloneRoleSubmit })
  }, [cloneRoleModal, onCloneRoleSubmit])

  const onRemoveClick = useCallback((rows) => {
    const selected: IAM_Permission[] = rows?.filter((row: RowInstance<IAM_Permission>) => !ARRAY.hasArray(row.original.children)).map((row: RowInstance<IAM_Permission>) => row.original)
    setExplicitPermissions(explicitPermissions.filter((permission) => !selected.find(({ name }: IAM_Permission) => permission.name === name)))
  }, [explicitPermissions])

  return (
    <>
      <Header {...{
        activeTabId,
        webTableRef,
        webTableState,
        apiTableRef,
        apiTableState,
        permissionsTree,
        webData,
        apiData,
        onAddPermissionsClick,
        onCloneRoleClick,
        remainingPermissions,
        onRemoveClick,
        viewOnly,
        title,
        enableSearch
      }}/>
      <div className='cm-border-l cm-border-r cm-border-t-0 cm-border-b-0 cm-border-gray-300 cm-border-solid'>
        <TabList activeTabId={activeTabId} onActiveChange={onActiveChange}>
          <Tab id={TABS.WEB.id}>{TABS.WEB.label}</Tab>
          {enableCustomAPI && <Tab id={TABS.API.id}>{TABS.API.label}</Tab>}
        </TabList>
      </div>
      <CONTAINER
        className='cm-border-l cm-border-r cm-border-t-0 cm-border-b cm-border-gray-300 cm-border-solid cm-rounded-b-lg cm-flex-1'
        style={{ overflow: 'hidden' }}
      >
        <TabPanel activeTabId={activeTabId} forTabId={TABS.WEB.id} addClassName='cm-h-full'>
          <WebTable
            data={webData}
            tableInstanceRef={webTableInstanceRef}
            tableRef={webTableRef}
            tableState={webTableState}
            viewOnly={viewOnly}
          />
        </TabPanel>
        {enableCustomAPI && (
          <TabPanel activeTabId={activeTabId} forTabId={TABS.API.id} addClassName='cm-h-full'>
            <APITable
              data={apiData}
              tableInstanceRef={apiTableInstanceRef}
              tableRef={apiTableRef}
              tableState={apiTableState}
              enablePagination
              viewOnly={viewOnly}
            />
          </TabPanel>
        )}
      </CONTAINER>
    </>
  )
}

AddPermissionsTable.propTypes = {}
export default AddPermissionsTable
